import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { getDeliveryOrderWindows } from "api/requests/getDeliveryOrderWindows";
import { Action, Category, Label } from "constants/events";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import { getSelectedScheduleWindow } from "redux/selectors/cart";
import { getCartDispensaryData } from "redux/selectors/cartDispensary";
import { convertDate, formatTimeWithTimezoneOffset } from "utils/dateUtils";

import Button from "components/botanic/Button/Button";
import { Modal } from "components/botanic/Modal";
import TrackImpression from "components/TrackImpression/TrackImpression";

import DateChip from "./DateChip";
import TimeSlot from "./TimeSlot";

export type ScheduleWindow = {
  end: string;
  isAvailable: boolean;
  start: string;
  remainingOrders?: number | null;
};

export type ScheduleDay = {
  date: string;
  isAvailable: boolean;
  windows: ScheduleWindow[];
};

type Props = {
  onDismissed: () => void;
  onSubmit: (selectedWindow?: ScheduleWindow) => void;
};

const handleSelectTimeWindow = (
  scheduledData: ScheduleDay[],
  startTime?: string,
) => {
  let selectedStartTime: ScheduleWindow | undefined;
  let selectedStartTimeIndex: number | undefined;
  // go through the full schedule
  // and and find based on startTime, or if not available, stop at the first day with the first available window
  scheduledData.find(({ windows }, index) => {
    if (startTime) {
      // find the window that matches the startTime
      selectedStartTime = windows.find((window) => window.start === startTime);
    } else {
      // find the first window that is available
      selectedStartTime = windows.find((window) => window.isAvailable);
    }
    if (selectedStartTime) {
      selectedStartTimeIndex = index;
    }
    return selectedStartTime;
  });
  return { selectedStartTime, selectedStartTimeIndex };
};

export const ScheduledOrderContent = ({ onSubmit }: Props) => {
  const [selectedDayIndex, setSelectedDayIndex] = React.useState(0);
  const [selectedScheduleWindow, setSelectedScheduleWindow] =
    React.useState<ScheduleWindow>();
  const [scheduledData, setScheduledData] = useState<ScheduleDay[]>([]);
  const [earliestAvailableTime, setEarliestAvailableTime] =
    React.useState<ScheduleWindow>();
  const dispensary = useSelector(getCartDispensaryData);
  const domainCountryCode = useDomainCountryCode();
  const previouslySelectedScheduleWindow = useSelector(
    getSelectedScheduleWindow,
  );
  const setPreviouslySelectedScheduleWindow = (
    scheduledData: ScheduleDay[],
  ) => {
    // update selected day index based on previously selected schedule window
    scheduledData.forEach((scheduleDay, index) => {
      const isSelectedScheduleWindowDay = scheduleDay.windows.find(
        ({ start }) => start === previouslySelectedScheduleWindow?.start,
      );
      if (isSelectedScheduleWindowDay) {
        setSelectedDayIndex(index);
      }
    });
    setSelectedScheduleWindow(previouslySelectedScheduleWindow);
  };
  useEffect(() => {
    getDeliveryOrderWindows(dispensary.id, domainCountryCode).then((data) => {
      setScheduledData(data);
      const {
        selectedStartTime: earliestAvailableTime,
        selectedStartTimeIndex,
      } = handleSelectTimeWindow(data);
      setEarliestAvailableTime(earliestAvailableTime);
      if (previouslySelectedScheduleWindow) {
        setPreviouslySelectedScheduleWindow(data);
      } else {
        setSelectedScheduleWindow(earliestAvailableTime);
        setSelectedDayIndex(selectedStartTimeIndex || selectedDayIndex);
      }
    });
  }, [dispensary, previouslySelectedScheduleWindow]);

  const selectedDay = React.useMemo(
    () => scheduledData[selectedDayIndex],
    [selectedDayIndex, scheduledData],
  );
  const { publishEvent } = useEventTracker();

  const label = React.useMemo(() => {
    switch (selectedDayIndex) {
      case 0:
        if (selectedScheduleWindow?.start === earliestAvailableTime?.start) {
          return "today_asap";
        }
        return "today_later";
      default:
        return `${Label.selectedOption} ${selectedDayIndex} day`;
    }
  }, [selectedDayIndex, selectedScheduleWindow, earliestAvailableTime, Label]);

  return scheduledData.length ? (
    <TrackImpression
      category={Category.schedulingModal}
      label={Label.modal}
      customDimensions={{ dispensaryId: dispensary?.id }}
    >
      <h2 className="font-extrabold text-md leading-7" id="date-header">
        Date
      </h2>
      <div
        className="flex justify-stretch gap-x-3 md:justify-between mt-2 items-center overflow-x-auto"
        role="radiogroup"
        aria-labelledby="date-header"
      >
        {scheduledData.map((dataPoint, index) => (
          <DateChip
            value={index}
            date={convertDate(dataPoint.date)}
            key={"date-chip-" + index}
            data-testid={"date-chip-" + index}
            disabled={!dataPoint.isAvailable}
            selected={selectedDayIndex === index}
            onSelected={() => {
              setSelectedDayIndex(index);
            }}
          />
        ))}
      </div>

      <h2 className="font-extrabold text-md mt-6" id="time-header">
        Time
      </h2>
      <div
        className="flex flex-col mt-2 md:h-96 md:overflow-y-auto"
        role="radiogroup"
        aria-labelledby="time-header"
      >
        {selectedDay?.isAvailable &&
          selectedDay.windows?.map((window, index) => (
            <TimeSlot
              key={window.start}
              time={`${formatTimeWithTimezoneOffset(
                window.start,
                dispensary.timeZone,
              )}-${formatTimeWithTimezoneOffset(
                window.end,
                dispensary.timeZone,
              )}`}
              data-testid={"time-slot-" + index}
              isAvailable={window.isAvailable}
              value={window.start}
              onChange={(startTime) => {
                const { selectedStartTime } = handleSelectTimeWindow(
                  scheduledData,
                  startTime,
                );
                setSelectedScheduleWindow(selectedStartTime);
              }}
              isSelected={selectedScheduleWindow?.start === window.start}
              isSoonest={window.start === earliestAvailableTime?.start}
            />
          ))}
      </div>
      <div className="p-4 pb-0 flex">
        <Button
          onClick={() => {
            publishEvent({
              action: Action.click,
              category: Category.schedulingModal,
              label,
            });
            onSubmit(selectedScheduleWindow);
          }}
          className="w-full max-w-none flex-grow"
        >
          select window
        </Button>
      </div>
    </TrackImpression>
  ) : null;
};

// Broke out the "Modal" vs Content, so we can test the content easier
export default (props: Props) => {
  const { publishEvent } = useEventTracker();
  const dispensary = useSelector(getCartDispensaryData);
  return (
    <Modal
      disableSwipeToClose={true}
      mobileFullscreen={true}
      onDismissed={() => {
        props.onDismissed();
        publishEvent({
          action: Action.click,
          category: Category.schedulingModal,
          dispensaryId: dispensary?.id,
          label: Label.closeSchedule,
        });
      }}
      title="Schedule Delivery"
      width={500}
    >
      <ScheduledOrderContent {...props} />
    </Modal>
  );
};
